import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/lib/useShop";
import { themeOf } from "@/lib/mall";
import { HeroBanner } from "@/components/shop/HeroBanner";
import { GroupCard } from "@/components/shop/GroupCard";

/**
 * The shop's home page (Phase 10): the thin hero banner, then "What do you need printed?" — one card per
 * product group this shop sells. A card opens that group's landing page, then the full-screen designer.
 */
export default function SimpleHome({ store }) {
    const mall = useMall();
    const { count } = useCart();
    const { shopSlug } = useShop();
    const slug = store?.slug || shopSlug;
    const location = useLocation();
    const added = !!location.state?.added;
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!slug) return;
        setData(null);
        api.get("/simple/cards", { params: { store: slug } })
            .then(({ data }) => setData(data)).catch(() => setData({ cards: [], store_card: null }));
    }, [slug]);

    const theme = themeOf(mall.store || store);
    const cards = data?.cards;
    const beforeCutoff = !!data?.before_cutoff;

    return (
        <>
            <HeroBanner card={data?.store_card || (store ? { name: store.name, city: store.city, banner: store.banner, tagline: store.tagline } : null)} theme={theme} />
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="simple-home">
                {added && (
                    <p className="mb-4 border-2 border-zinc-900 px-3 py-2 text-base" data-testid="added-line">
                        Added · <Link to="/cart" className="font-semibold underline" data-testid="added-cart-link">Cart ({count})</Link>
                    </p>
                )}
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <h2 className="font-display text-2xl font-black uppercase tracking-tight text-zinc-900 md:text-3xl" data-testid="simple-home-title">What do you need printed?</h2>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="simple-home-count">
                        {cards ? `${cards.length} ways to order · pick one` : ""}
                    </p>
                </div>
                {store && store.accepts_cards === false && (
                    <p className="mt-4 border-2 border-zinc-900 px-3 py-2 text-base" data-testid="home-opening-soon">
                        <span className="font-semibold">Opening soon.</span> Checkout opens when payments are set up.{store.phone ? <> Call or text <a className="underline" href={`sms:${store.phone.replace(/[^\d+]/g, "")}`}>{store.phone}</a>.</> : null}
                    </p>
                )}
                {!cards ? (
                    <p className="mt-6 text-zinc-500" data-testid="simple-home-loading">Loading…</p>
                ) : (
                    <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="simple-grid">
                        {cards.map((c) => (
                            <GroupCard key={c.key} to={c.to} image={c.image} title={c.title} theme={theme} testid={`simple-card-${c.key}`} idPrefix={`simple-card-${c.key}`}
                                hook={c.from_price != null ? `from ${c.hook}` : c.hook} badge={c.same_day && beforeCutoff ? "Ready today" : null} />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}
